import { forwardRef, useEffect, useState } from 'react';

import { Text } from '@gympass/yoga';
import { Button, Typography } from '@mui/material';
import { wellzTheme } from '@wellz-theme/mui';
import groupBy from 'lodash.groupby';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatDate, formatHour } from 'utils/helpers';
import { objectSortFn } from 'utils/helpers/sorting';

import useTimeFormat from 'hooks/useTimeFormat';
import { ScheduledMessage } from 'services/index';

import { ChatMessageLoadingSkeleton } from 'components/ChatMessageLoadingSkeleton';
import MessageBlock from 'components/MessageBlock/MessageBlock';
import Retention from 'images/retention.svg';
import { DeleteScheduledMessage } from 'modals/DeleteScheduledMessage';

import { ScheduledMessageConstants } from './constants';

import { ChatConversationBodyProps } from './types';

import * as S from './styles';

const ChatConversationBody = forwardRef<
  HTMLDivElement,
  ChatConversationBodyProps
>(
  (
    {
      loading,
      messages,
      cpIdentity,
      onScrollNext,
      patientTyping,
      hasMoreMessages,
      patientIdentity,
      messageHandler,
      scheduledMessage,
      ...props
    },
    ref,
  ) => {
    const [messageScheduled, setMessageScheduled] = useState<
      ScheduledMessage | undefined
    >();
    const [
      deleteScheduledMessageModalIsOpen,
      setDeleteScheduledMessageModalIsOpen,
    ] = useState(false);

    const { formatToDate } = useTimeFormat();

    const intl = useIntl();

    const listMessages = groupBy(
      objectSortFn(messages, 'dateCreated', 'DESC', 'date'),
      item => formatToDate(item.dateCreated?.toISOString()),
    );
    const items = Object.keys(listMessages);

    const handleOpenDeleteScheduledMessageModal = () => {
      setDeleteScheduledMessageModalIsOpen(true);
    };

    const handleCloseDeleteMessageModal = () => {
      setDeleteScheduledMessageModalIsOpen(false);
    };

    const handleMessageDeleted = async () => {
      setMessageScheduled(undefined);
    };

    useEffect(() => {
      setMessageScheduled(scheduledMessage);
    }, [scheduledMessage]);

    const triggerCode = scheduledMessage?.trigger.trigger_code;
    const suggestedMessage =
      ScheduledMessageConstants[triggerCode || 'generic'];

    return (
      <>
        {deleteScheduledMessageModalIsOpen && (
          <DeleteScheduledMessage
            id={String(messageScheduled?.id)}
            isOpen={deleteScheduledMessageModalIsOpen}
            onClose={handleCloseDeleteMessageModal}
            onDelete={handleMessageDeleted}
          />
        )}

        <S.ChatBackground {...props}>
          <S.InfitineScrollContainer id="scrollableChat">
            <div ref={ref} />

            <InfiniteScroll
              style={{
                display: 'flex',
                flexDirection: 'column-reverse',
              }}
              inverse
              dataLength={messages?.length ?? 0}
              next={() => {
                if (onScrollNext) {
                  onScrollNext();
                }
              }}
              hasMore={!!hasMoreMessages}
              scrollableTarget="scrollableChat"
              loader={<ChatMessageLoadingSkeleton />}
              scrollThreshold="2000px"
            >
              {!!messageScheduled && (
                <S.ScheduledMessageWrapper disableGutters>
                  <S.ScheduledMessageHeader
                    expandIcon={<S.ScheduledMessageHeaderIcon />}
                  >
                    <Typography fontWeight={500}>
                      {intl.formatMessage({
                        id: suggestedMessage.title,
                      })}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={wellzTheme.palette.text.secondary}
                    >
                      {intl.formatMessage(
                        {
                          id: suggestedMessage.subtitle,
                        },
                        {
                          date: formatDate(
                            new Date(messageScheduled.schedule_time),
                            { locale: intl.locale },
                          ),
                          time: formatHour(
                            new Date(messageScheduled.schedule_time),
                            { omitMinutesWhenZero: false, locale: intl.locale },
                          ),
                          b: (...chunks) => (
                            <Typography
                              variant="caption"
                              fontWeight={500}
                              color={wellzTheme.palette.text.secondary}
                            >
                              {chunks}
                            </Typography>
                          ),
                        },
                      )}
                    </Typography>
                  </S.ScheduledMessageHeader>
                  <S.ScheduledMessageContainer>
                    <S.ScheduledMessage>
                      <Typography
                        variant="body2"
                        dangerouslySetInnerHTML={{
                          __html: messageScheduled.final_message,
                        }}
                      />
                    </S.ScheduledMessage>

                    <Button
                      startIcon={<S.TrashIcon />}
                      onClick={handleOpenDeleteScheduledMessageModal}
                    >
                      {intl.formatMessage({
                        id: 'MESSAGETIPS.EDITSCHEDULE.DELETE',
                      })}
                    </Button>
                  </S.ScheduledMessageContainer>
                </S.ScheduledMessageWrapper>
              )}

              {loading && <ChatMessageLoadingSkeleton />}
              {!!items?.length &&
                items.map((item, index, array) => (
                  <MessageBlock
                    key={item}
                    titleDate={item}
                    cpIdentity={cpIdentity}
                    data-testid="message-block"
                    messageHandler={messageHandler}
                    patientIdentity={patientIdentity}
                    filteredMessages={listMessages[item]}
                    patientTyping={patientTyping && index === array.length - 1}
                  />
                ))}
            </InfiniteScroll>
          </S.InfitineScrollContainer>
        </S.ChatBackground>

        {!items.length && !scheduledMessage && !loading && (
          <S.PlaceholderWrapper data-testid="no-messages-img">
            <S.ChatPlaceholder>
              <Retention />
              <Text color="medium" mt="8">
                <FormattedMessage id="CHAT.CONVERSATION.PLACEHOLDER" />
              </Text>
            </S.ChatPlaceholder>
          </S.PlaceholderWrapper>
        )}
      </>
    );
  },
);

ChatConversationBody.displayName = 'ChatConversationBody';

export default ChatConversationBody;
